"use client";

import { useState, useEffect, useCallback, useRef } from "react";

const sliderData = [
  {
    img: "/images/slider1.jpg",
    title: "Where Excellence Begins",
    subtitle: "A modern school shaping the future of young learners.",
  },
  {
    img: "/images/slider2.jpg",
    title: "Transforming Learning for Tomorrow",
    subtitle: "Smart classrooms, engaging curriculum, and holistic growth.",
  },
  {
    img: "/images/slider3.jpg",
    title: "Smart Education for a Smarter Future",
    subtitle: "Interactive learning that builds confidence and curiosity.",
  },
  {
    img: "/images/slider4.jpg",
    title: "Building Leaders, Innovators & Thinkers",
    subtitle: "A strong foundation for lifelong success.",
  },
];

/**
 * Custom hook to manage slider state and auto-progression.
 * @param slideCount - The total number of slides.
 * @param interval - The duration for each slide in milliseconds.
 */
const useSlider = (slideCount: number, interval: number = 5000) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const slideRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear existing timer
    if (slideRef.current) clearTimeout(slideRef.current);

    // Slide transition timer
    slideRef.current = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % slideCount);
    }, interval);

    return () => {
      if (slideRef.current) clearTimeout(slideRef.current);
    };
  }, [currentIndex, slideCount, interval]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slideCount);
  }, [slideCount]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slideCount) % slideCount);
  }, [slideCount]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  return { currentIndex, nextSlide, prevSlide, goToSlide };
};

const Slide = ({ 
  data, 
  isActive 
}: { 
  data: (typeof sliderData)[0]; 
  isActive: boolean 
}) => (
  <div data-component-id="src/app/HeroSection.tsx:73:2" data-component-path="src/app/HeroSection.tsx" data-component-line="73" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
      isActive ? "opacity-100 z-10" : "opacity-0 z-0"
    }`}
    aria-hidden={!isActive}
  >
    <img data-component-id="src/app/HeroSection.tsx:79:4" data-component-path="src/app/HeroSection.tsx" data-component-line="79" data-component-file="HeroSection.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%20object-center%22%7D"
      src={data.img}
      alt={data.title}
      className="w-full h-full object-cover object-center"
      loading={isActive ? "eager" : "lazy"}
    />
    <div data-component-id="src/app/HeroSection.tsx:85:4" data-component-path="src/app/HeroSection.tsx" data-component-line="85" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20flex-col%20justify-center%20px-4%20sm%3Apx-12%20lg%3Apx-20%20text-white%20bg-black%2F30%22%7D" className="absolute inset-0 flex flex-col justify-center px-4 sm:px-12 lg:px-20 text-white bg-black/30">
      <h2 data-component-id="src/app/HeroSection.tsx:86:6" data-component-path="src/app/HeroSection.tsx" data-component-line="86" data-component-file="HeroSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20sm%3Atext-3xl%20md%3Atext-4xl%20lg%3Atext-5xl%20font-bold%20leading-tight%20drop-shadow-xl%20animate-fade-in-down%22%7D" className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight drop-shadow-xl animate-fade-in-down">
        {data.title}
      </h2>
      <p data-component-id="src/app/HeroSection.tsx:89:6" data-component-path="src/app/HeroSection.tsx" data-component-line="89" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-2%20text-sm%20sm%3Atext-base%20md%3Atext-lg%20lg%3Atext-xl%20opacity-90%20max-w-xl%20drop-shadow%20animate-fade-in-up%22%7D" className="mt-2 text-sm sm:text-base md:text-lg lg:text-xl opacity-90 max-w-xl drop-shadow animate-fade-in-up">
        {data.subtitle}
      </p>
    </div>
  </div>
);

const SlideIndicators = ({ 
  currentIndex,
  totalSlides,
  onDotClick
}: { 
  currentIndex: number;
  totalSlides: number;
  onDotClick: (index: number) => void;
}) => (
  <div data-component-id="src/app/HeroSection.tsx:105:2" data-component-path="src/app/HeroSection.tsx" data-component-line="105" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-4%20left-1%2F2%20-translate-x-1%2F2%20z-20%20flex%20gap-2%22%7D" className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
    {Array.from({ length: totalSlides }).map((_, index) => (
      <button data-component-id="src/app/HeroSection.tsx:107:6" data-component-path="src/app/HeroSection.tsx" data-component-line="107" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
        key={index}
        onClick={() => onDotClick(index)}
        className={`w-2 h-2 rounded-full transition-all duration-300 ${
          index === currentIndex 
            ? "bg-white scale-125" 
            : "bg-white/50 hover:bg-white/70"
        }`}
        aria-label={`Go to slide ${index + 1}`}
      />
    ))}
  </div>
);

const NavigationArrows = ({ 
  onPrev, 
  onNext 
}: { 
  onPrev: () => void; 
  onNext: () => void;
}) => (
  <>
    {/* Previous Button - Left side */}
    <button data-component-id="src/app/HeroSection.tsx:130:4" data-component-path="src/app/HeroSection.tsx" data-component-line="130" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20left-4%20top-1%2F2%20-translate-y-1%2F2%20z-20%20flex%20items-center%20justify-center%20w-10%20h-10%20sm%3Aw-12%20sm%3Ah-12%20rounded-full%20bg-white%2F10%20border%20border-white%2F20%20text-white%20hover%3Abg-white%2F20%20transition%22%7D"
      onClick={onPrev}
      className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition"
      aria-label="Previous Slide"
    >
      <svg data-component-id="src/app/HeroSection.tsx:135:6" data-component-path="src/app/HeroSection.tsx" data-component-line="135" data-component-file="HeroSection.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%20sm%3Aw-6%20sm%3Ah-6%22%7D" className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth={2}>
        <path data-component-id="src/app/HeroSection.tsx:136:8" data-component-path="src/app/HeroSection.tsx" data-component-line="136" data-component-file="HeroSection.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    {/* Next Button - Right side */}
    <button data-component-id="src/app/HeroSection.tsx:141:4" data-component-path="src/app/HeroSection.tsx" data-component-line="141" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-4%20top-1%2F2%20-translate-y-1%2F2%20z-20%20flex%20items-center%20justify-center%20w-10%20h-10%20sm%3Aw-12%20sm%3Ah-12%20rounded-full%20bg-white%2F10%20border%20border-white%2F20%20text-white%20hover%3Abg-white%2F20%20transition%22%7D"
      onClick={onNext}
      className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition"
      aria-label="Next Slide"
    >
      <svg data-component-id="src/app/HeroSection.tsx:146:6" data-component-path="src/app/HeroSection.tsx" data-component-line="146" data-component-file="HeroSection.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%20sm%3Aw-6%20sm%3Ah-6%22%7D" className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth={2}>
        <path data-component-id="src/app/HeroSection.tsx:147:8" data-component-path="src/app/HeroSection.tsx" data-component-line="147" data-component-file="HeroSection.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  </>
);



const serviceCardsData = [
  { id: "1", icon: "/images/kick.png", number: "1", description: "Quality education for a brighter future." },
  { id: "2", icon: "/images/basketball.png", number: "2", description: "Nurturing young minds for a better tomorrow." },
  { id: "3", icon: "/images/ball.png", number: "3", description: "A place where learning is fun and interactive." },
  { id: "4", icon: "/images/cricket.png", number: "4", description: "Fostering creativity and critical thinking." },
];

const ServiceCard = ({ card }: { card: (typeof serviceCardsData)[0] }) => (
  <div data-component-id="src/app/HeroSection.tsx:163:2" data-component-path="src/app/HeroSection.tsx" data-component-line="163" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20justify-center%20items-center%20w-%5Bcalc(50%25-5px)%5D%20sm%3Aw-auto%20sm%3Amin-w-%5B222px%5D%20bg-background-light%20border%20border-%5B%23106d63%5D%20rounded-%5B19px%5D%20sm%3Arounded-%5B38px%5D%20p-%5B10px%5D%20sm%3Ap-%5B20px%5D%22%7D"
    className="flex flex-col justify-center items-center w-[calc(50%-5px)] sm:w-auto sm:min-w-[222px] bg-background-light border border-[#106d63] rounded-[19px] sm:rounded-[38px] p-[10px] sm:p-[20px]"
  >
    <div data-component-id="src/app/HeroSection.tsx:166:4" data-component-path="src/app/HeroSection.tsx" data-component-line="166" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-%5B8px%5D%20sm%3Agap-%5B16px%5D%20w-full%22%7D" className="flex flex-col gap-[8px] sm:gap-[16px] w-full">
      <div data-component-id="src/app/HeroSection.tsx:167:6" data-component-path="src/app/HeroSection.tsx" data-component-line="167" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-center%22%7D" className="flex justify-between items-center">
        {card.icon && (
          <img data-component-id="src/app/HeroSection.tsx:169:10" data-component-path="src/app/HeroSection.tsx" data-component-line="169" data-component-file="HeroSection.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22alt%22%3A%22%22%2C%22className%22%3A%22w-%5B30px%5D%20h-%5B30px%5D%20sm%3Aw-%5B45px%5D%20sm%3Ah-%5B45px%5D%22%7D" src={card.icon} alt="" width="45" height="45" className="w-[30px] h-[30px] sm:w-[45px] sm:h-[45px]" />
        )}
        <span data-component-id="src/app/HeroSection.tsx:171:8" data-component-path="src/app/HeroSection.tsx" data-component-line="171" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B16px%5D%20sm%3Atext-%5B33px%5D%20font-%5B'Bungee'%5D%20text-text-accent%22%2C%22textContent%22%3A%220%22%7D" className="text-[16px] sm:text-[33px] font-['Bungee'] text-text-accent">
          0{card.number}
        </span>
      </div>
      <p data-component-id="src/app/HeroSection.tsx:175:6" data-component-path="src/app/HeroSection.tsx" data-component-line="175" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B10px%5D%20sm%3Atext-%5B20px%5D%20font-%5B'Poppins'%5D%20text-text-secondary%22%7D" className="text-[10px] sm:text-[20px] font-['Poppins'] text-text-secondary">
        {card.description}
      </p>
    </div>
  </div>
);

const HeroSection = () => {
  const { currentIndex, nextSlide, prevSlide, goToSlide } = useSlider(sliderData.length);

  return (
    <section data-component-id="src/app/HeroSection.tsx:186:4" data-component-path="src/app/HeroSection.tsx" data-component-line="186" data-component-file="HeroSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22w-full%20relative%20mt-0%20pt-16%20mb-64%20sm%3Amb-48%22%7D" className="w-full relative mt-0 pt-16 mb-64 sm:mb-48">
      <div data-component-id="src/app/HeroSection.tsx:187:6" data-component-path="src/app/HeroSection.tsx" data-component-line="187" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20mx-auto%20p-0%22%7D" className="w-full mx-auto p-0">
        {/* Slider Container - Optimized for 1920x900 images */}
        <div data-component-id="src/app/HeroSection.tsx:189:8" data-component-path="src/app/HeroSection.tsx" data-component-line="189" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20aspect-%5B1920%2F900%5D%20max-h-%5B900px%5D%20m-0%20p-0%20overflow-hidden%22%7D" className="relative w-full aspect-[1920/900] max-h-[900px] m-0 p-0 overflow-hidden">
          {/* For mobile: Ensure full image display */}
          <div data-component-id="src/app/HeroSection.tsx:191:10" data-component-path="src/app/HeroSection.tsx" data-component-line="191" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20md%3Ahidden%22%7D" className="absolute inset-0 md:hidden">
            {sliderData.map((slide, i) => (
              <div data-component-id="src/app/HeroSection.tsx:193:14" data-component-path="src/app/HeroSection.tsx" data-component-line="193" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                key={i}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  i === currentIndex ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
                aria-hidden={i !== currentIndex}
              >
                <img data-component-id="src/app/HeroSection.tsx:200:16" data-component-path="src/app/HeroSection.tsx" data-component-line="200" data-component-file="HeroSection.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%20object-center%22%7D"
                  src={slide.img}
                  alt={slide.title}
                  className="w-full h-full object-cover object-center"
                  loading={i === currentIndex ? "eager" : "lazy"}
                />
              </div>
            ))}
          </div>

          {/* For desktop: Optimized for 1920x900 */}
          <div data-component-id="src/app/HeroSection.tsx:211:10" data-component-path="src/app/HeroSection.tsx" data-component-line="211" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20md%3Ablock%22%7D" className="hidden md:block">
            {sliderData.map((slide, i) => (
              <Slide data-component-id="src/app/HeroSection.tsx:213:14" data-component-path="src/app/HeroSection.tsx" data-component-line="213" data-component-file="HeroSection.tsx" data-component-name="Slide" data-component-content="%7B%22elementName%22%3A%22Slide%22%7D" key={i} data={slide} isActive={i === currentIndex} />
            ))}
          </div>

          {/* Navigation Arrows and Slide Indicators */}
          <NavigationArrows data-component-id="src/app/HeroSection.tsx:218:10" data-component-path="src/app/HeroSection.tsx" data-component-line="218" data-component-file="HeroSection.tsx" data-component-name="NavigationArrows" data-component-content="%7B%22elementName%22%3A%22NavigationArrows%22%7D" 
            onPrev={prevSlide} 
            onNext={nextSlide}
          />
          <SlideIndicators data-component-id="src/app/HeroSection.tsx:222:10" data-component-path="src/app/HeroSection.tsx" data-component-line="222" data-component-file="HeroSection.tsx" data-component-name="SlideIndicators" data-component-content="%7B%22elementName%22%3A%22SlideIndicators%22%7D" 
            currentIndex={currentIndex}
            totalSlides={sliderData.length}
            onDotClick={goToSlide}
          />
        </div>

        {/* Service Cards Container - KEPT EXACTLY THE SAME LAYOUT */}
        <div data-component-id="src/app/HeroSection.tsx:230:8" data-component-path="src/app/HeroSection.tsx" data-component-line="230" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-%5B-240px%5D%20sm%3Abottom-%5B-120px%5D%20w-%5B90%25%5D%20sm%3Aw-auto%20left-1%2F2%20sm%3Aleft-%5B300px%5D%20-translate-x-1%2F2%20sm%3Atranslate-x-0%20right-auto%20sm%3Aright-%5B30px%5D%20z-20%22%7D" className="absolute bottom-[-240px] sm:bottom-[-120px] w-[90%] sm:w-auto left-1/2 sm:left-[300px] -translate-x-1/2 sm:translate-x-0 right-auto sm:right-[30px] z-20">
          <div data-component-id="src/app/HeroSection.tsx:231:10" data-component-path="src/app/HeroSection.tsx" data-component-line="231" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20justify-center%20gap-%5B10px%5D%20sm%3Aflex-nowrap%20sm%3Agap-%5B20px%5D%20sm%3Aoverflow-x-auto%20pb-4%20sm%3Apb-0%20scrollbar-hide%22%7D" className="flex flex-wrap justify-center gap-[10px] sm:flex-nowrap sm:gap-[20px] sm:overflow-x-auto pb-4 sm:pb-0 scrollbar-hide">
            {serviceCardsData.map((card) => (
              <ServiceCard data-component-id="src/app/HeroSection.tsx:233:14" data-component-path="src/app/HeroSection.tsx" data-component-line="233" data-component-file="HeroSection.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%7D" key={card.id} card={card} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;